import { Apple, Play } from 'lucide-react'
import QRCode from 'qrcode'
import { useEffect, useState } from 'react'
import { ANDROID_APP_URL, IOS_APP_URL } from '../constants/appStoreLinks'

/**
 * One store's QR code: generated client-side with `qrcode` (as AggregatorPortalPage's invite QR
 * already does) rather than fetched from a third-party QR image API, so it works offline and
 * never depends on an outside service staying up.
 */
function useAppStoreQr(url: string) {
  const [qr, setQr] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    QRCode.toDataURL(url, { width: 200, margin: 1 })
      .then((dataUrl) => !cancelled && setQr(dataUrl))
      .catch(() => !cancelled && setQr(null))
    return () => {
      cancelled = true
    }
  }, [url])
  return qr
}

/**
 * The sidebar's "scan to download the app" card, sitting under the partner links - same spot the
 * reference Career Launcher rail uses for its own app-download QR pair.
 */
export function AppDownloadQr() {
  const iosQr = useAppStoreQr(IOS_APP_URL)
  const androidQr = useAppStoreQr(ANDROID_APP_URL)

  return (
    <div className="mt-3 rounded-2xl bg-gradient-to-br from-ink-900 to-ink-700 p-3 text-center text-white">
      <p className="text-[11px] font-bold leading-snug">Download the YellowDots AI App Now!</p>
      <div className="mt-2.5 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-white p-1.5">
          {iosQr ? (
            <img src={iosQr} alt="QR code to download YellowDots AI on the App Store" className="mx-auto h-auto w-full" />
          ) : (
            <div className="aspect-square w-full animate-pulse rounded-md bg-ink-100" />
          )}
          <a
            href={IOS_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 flex items-center justify-center gap-1 rounded-md bg-ink-900 px-1.5 py-1 text-[10px] font-bold text-white"
          >
            <Apple size={11} /> iOS
          </a>
        </div>
        <div className="rounded-xl bg-white p-1.5">
          {androidQr ? (
            <img src={androidQr} alt="QR code to download YellowDots AI on Google Play" className="mx-auto h-auto w-full" />
          ) : (
            <div className="aspect-square w-full animate-pulse rounded-md bg-ink-100" />
          )}
          <a
            href={ANDROID_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 flex items-center justify-center gap-1 rounded-md bg-ink-900 px-1.5 py-1 text-[10px] font-bold text-white"
          >
            <Play size={11} className="fill-emerald-400 text-emerald-400" /> Android
          </a>
        </div>
      </div>
    </div>
  )
}
